import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';

import {
  Table,
  Stack,
  Typography,
  Grid,
  Backdrop,
  CircularProgress,
} from '@mui/material';

import MUIDataTable from 'mui-datatables';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import Iconify from '../../../../iconify';

const { PRIVATE_ROUTE } = API_ROUTES;

export default function DefaultList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortEnable, setSortEnable] = useState({});
  const [searchEnable, setSearchEnable] = useState('');

  const { authAxios } = useContext(FetchContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch Invoice Items and Tax Codes.
  const getUserdata = useCallback(
    async (query = {}) => {
      try {
        setLoading(true);

        const pageNo = query.page_no ?? 0;
        const searchQuery =
          typeof query === 'string'
            ? query
            : query.searchQuery ?? '';

        const sortField =
          query.sortField ?? sortEnable.sortField ?? '';

        const sortOrder =
          query.sortOrder ?? sortEnable.sortOrder ?? '';

        const params = new URLSearchParams({
          page: String(pageNo + 1),
          per_page: String(rowsPerPage),
          sortField,
          sortOrder,
          search: searchQuery,
        });

        const { data } = await authAxios.get(
          `${PRIVATE_ROUTE.GET_INVOICE_DEFAULTS}?${params.toString()}`
        );

        const invoiceItems = (data?.invoiceItems || []).map((item) => ({
          _id: item._id,
          type: 'Invoice Item',
          name: item.Name || '',
        }));

        const taxcodes = (data?.taxcodes || []).map((item) => ({
          _id: item._id,
          type: 'Tax Code',
          name: item.Name || '',
        }));

        // Combine the two source collections.
        let allRows = [...invoiceItems, ...taxcodes];

        // Search the combined results.
        if (searchQuery) {
          const term = searchQuery.toLowerCase();

          allRows = allRows.filter((row) =>
            `${row.type} ${row.name}`.toLowerCase().includes(term)
          );
        }

        // Sort the combined results.
        if (sortField && sortOrder) {
          allRows.sort((a, b) => {
            const first = String(a[sortField] ?? '').toLowerCase();
            const second = String(b[sortField] ?? '').toLowerCase();

            const comparison = first.localeCompare(second);

            return sortOrder === 'desc'
              ? -comparison
              : comparison;
          });
        }

        // Paginate after combining the collections.
        const startIndex = pageNo * rowsPerPage;
        const pageRows = allRows.slice(
          startIndex,
          startIndex + rowsPerPage
        );

        setUsers(pageRows);
        setCount(allRows.length);
      } catch (error) {
        console.error('Failed to fetch Invoice Defaults:', {
          message: error.message,
          status: error.response?.status,
          response: error.response?.data,
        });

        setUsers([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    },
    [authAxios, rowsPerPage, sortEnable]
  );

  // Load and refresh the table.
  useEffect(() => {
    getUserdata({
      page_no: page,
      searchQuery: searchEnable,
      sortField: sortEnable.sortField,
      sortOrder: sortEnable.sortOrder,
    });
  }, [
    getUserdata,
    page,
    searchEnable,
    sortEnable.sortField,
    sortEnable.sortOrder,
    location.state?.refresh,
  ]);

  // Open the edit form.
  const handleEditRedirect = (row) => {
    navigate(`/master/Invoice_Defaults/update/${row._id}`, {
      state: {
        backgroundLocation: location,
        defaultRecord: {
          _id: row._id,
          sourceId: row._id,
          type:
            row.type === 'Tax Code'
              ? 'taxcode'
              : 'invoice items',
          Name: row.name,
          name: row.name,
        },
      },
    });
  };

  // Delete the source record.
  const handleRowDelete = async (row) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${row.name}"?`
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      const type =
        row.type === 'Tax Code'
          ? 'taxcode'
          : 'invoice items';

      await authAxios.post(
        `${PRIVATE_ROUTE.DELETE_INVOICE_DEFAULTS}${row._id}`,
        {
          _id: row._id,
          type,
        }
      );

      await getUserdata({
        page_no: page,
        searchQuery: searchEnable,
        sortField: sortEnable.sortField,
        sortOrder: sortEnable.sortOrder,
      });
    } catch (error) {
      console.error('Failed to delete Invoice Default:', {
        message: error.message,
        status: error.response?.status,
        response: error.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  // Columns styled and structured like OfficeList.
  const columns = [
    {
      name: 'type',
      label: 'Type',
      options: {
        draggable: true,
        sort: true,
        filter: true,
        display: true,
      },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        draggable: true,
        sort: true,
        filter: true,
        display: true,
      },
    },
    {
      name: '_id',
      label: '_id',
      options: {
        draggable: false,
        sort: false,
        filter: false,
        display: false,
      },
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        filter: false,
        display: true,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData[2];
          const row = users.find(
            (item) => String(item._id) === String(id)
          );

          if (!row) return null;

          return (
            <div style={{ display: 'flex', gap: '5px' }}>
              <EditIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => handleEditRedirect(row)}
              />

              <DeleteIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => handleRowDelete(row)}
              />
            </div>
          );
        },
      },
    },
  ];

  // OfficeList table options.
  const options = {
    selectToolbarPlacement: 'none',
    tableId: 'InvoiceDefaultsTable',
    rowHover: false,
    jumpToPage: true,
    responsive: 'standard',
    print: true,
    filter: false,
    fixedHeader: true,
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50],
    searchOpen: true,
    search: true,
    selectableRows: 'none',
    serverSide: true,

    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
      },
    },

    setRowProps: (row, dataIndex, rowIndex) => ({
      style: {
        backgroundColor: rowIndex % 2 === 0 ? 'rgba(101, 200, 208,0.15) 200, 208,0.15)' : 'white',
      },
    }),

    setTableProps: () => ({
      padding: 'default',
        border: '1px solid rgba(101, 200, 208,0.15) 200, 208,0.15)',
        size: 'small',
    }),

    onSearchChange: (searchQuery) => {
      setSearchEnable(searchQuery || '');
      setPage(0);
    },

    onChangeRowsPerPage: (numberOfRows) => {
      setRowsPerPage(numberOfRows);
      setPage(0);
    },

    onChangePage: (currentPage) => {
      setPage(currentPage);
    },

    onColumnSortChange: (changedColumn, direction) => {
      setSortEnable({
        sortField: changedColumn,
        sortOrder: direction,
      });
      setPage(0);
    },

    textLabels: {
      body: {
        noMatch: loading
          ? 'Loading data...'
          : 'No matching records found',
      },
    },
  };

  return (
    <>
      <Grid sx={{ p: 4, pb: 0 }} direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" gutterBottom>
            Invoice Defaults
          </Typography>
          {/* <Button variant="contained" onClick={handlejobcreateform} startIcon={<Iconify icon="eva:plus-fill" />}>
            New Office
          </Button> */}
        </Stack>
        <Table>
          <MUIDataTable options={options} columns={columns} data={users} />
          
          {loading && (
            <div>
              <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </div>
          )}
        </Table>
      </Grid>
    </>
  );
}